import React, { memo, useCallback, useEffect, useRef, useState } from "react";
import { observer } from "mobx-react";
import { useNarrativesContext } from "@components/layout/main-layout/private-layout";
import TheoryOfChangeForm from "@components/pages/project/single-functions/theory-of-change/form";
import CommonFooterButton from "@commonComponents/footer-button";
import { useStore } from "@stores/root-store";
import { constRoute } from "@utils/route";
import { useForm } from "antd/es/form/Form";
import { useNavigate, useParams } from "react-router-dom";
import { io } from "socket.io-client";
import { DataType } from "../../../../../../types/data-type";
import { baseSocketUrl } from "@api/const";
import { notification } from "@utils/notifications";

interface TheoryOfChangeFormProps {}
const formQuestions = [
  {
    q: (
      <span>
        Enter the Project’s <i>Goal Statement Review</i> Below:
      </span>
    ),
    result: <span>Your Results Below:</span>,
    limit: 1000,
    placeholder:
      "Paste the sentence or short paragraph that describes your projects ultimate, long-term goal in the space below.\n\n" +
      "Guidance:\n\n" +
      "For the most effective evaluation, ensure your goal is specific, measurable, and directly relevant to a clear climate change challenge. Your statement should \nalso describe a long-term impact that is both well-defined and achievable.\n\n" +
      "A great way to structure this is by following a clear causal logic:\n\n" +
      "IF (The Core Intervention): What is the main thing your project will deliver or change?\n\n" +
      "THEN (The Measurable Impact): What specific, long-term change will happen as a result?\n\n" +
      "BECAUSE (The Rationale): Why do you believe this change will occur?",
  },
  {
    q: (
      <>
        Enter  Project’s <i>Log-frame Review/IRMF</i> Below:
      </>
    ),
    result: <>Your Results Below:</>,
    limit: 1000,
    placeholder:
      "Paste your Logical Framework text here.\n\n" +
      "Guidance:\n\n" +
      "For a helpful review of your project's logic, please describe the key elements of your results chain, showing how they connect.\n\n" +
      " • Goal: The overall long-term impact.\n\n" +
      " • Outcomes: The key changes that result from the project.\n\n" +
      " • Outputs: The tangible products/services delivered to achieve the outcomes.\n\n" +
      " • Activities: The main tasks performed to produce the outputs.\n\n" +
      "Climate Finance Copilot will assess if your activities logically lead to your outputs, outcomes and goals.",
  },
  {
    q: (
      <>
        Enter the Project’s <i>Barriers and Risks Review</i> Below:
      </>
    ),
    result: <>Your Results Below:</>,
    placeholder:
      "Paste your Barriers and Risks section here.\n\n" +
      "Guidance:\n\n" +
      "To get a high-quality review, provide your analysis of the challenges your project faces. This includes:\n\n" +
      " • Barriers: The existing hurdles your project must overcome to succeed. Think about what makes the current situation difficult (e.g., institutional weaknesses,\n\u00A0\u00A0\u00A0 social norms, market failures).\n\n" +
      " • Risks: The potential future events that could derail your project. For each major risk, provide a full mitigation plan, including its category (e.g., financial,\n\u00A0\u00A0\u00A0 operational), its probability and impact, and the concrete steps you will take to manage it.",
    limit: 1000,
  },
  {
    q: (
      <>
        Enter Project's <i>Assumptions Validation</i> Below:
      </>
    ),
    result: <>Your Results Below:</>,
    limit: 1000,
    placeholder:
      "Paste your Assumptions section here.\n\n" +
      "Guidance:\n\n" +
      "To get a high-quality review, provide the list of assumptions that underpin your project's logic. These are conditions that are outside your direct control but are \nnecessary for success (e.g., 'The national government will maintain stable renewable energy policies').\n\n" +
      "Beyond just listing them, the most critical information you can provide is your management plan: How will you check if these assumptions hold true during \nimplementation, and what is your backup plan if they don’t?",
  },
  {
    q: (
      <>
        Enter Project's <i>Enhanced Log-frame</i> Below:{" "}
      </>
    ),
    result: <>Your Results Below:</>,
    limit: 1000,
    placeholder: [
      "Please paste your enhanced log-frame narrative below.\n\n" +
        "Guidance:\n\n" +
        "Provide the core components of your project's logic below. Copilot will analyze your input, refine the language, develop SMART indicators, and structure it into \na professional narrative.\n\n" +
        " • Goal: What is the ultimate long-term impact your project aims to achieve?\n\n" +
        " • Outcomes: What are the key medium-term changes that will result from your project?\n\n" +
        " • Outputs: What tangible products or services will you deliver to cause those changes?\n\n" +
        " • Activities: What are the main actions you will take to create those products and services?",
    ].join(""),
  },
];

export const sectionsMap = [
  {
    text: "Goal Statement Review",
    section: "GSR",
  },
  {
    text: "Log-frame Review/IRMF",
    section: "AAC",
  },
  {
    text: "Barriers and Risks Review",
    section: "BR",
  },
  {
    text: "Assumptions Validation",
    section: "A",
  },
  {
    text: "Enhanced Log-frame",
    section: "NLF",
  },
];

const sections = sectionsMap.map((section) => section.section);

const TheoryOfChangePage: React.FC<TheoryOfChangeFormProps> = () => {
  const nav = useNavigate();
  const socketRef = useRef(null);
  const { type, step } = useParams();
  const [form] = useForm();
  const {
    user: { getSingleProjectData, projectNameData },
    modelType: { getSelectedModelType },
  } = useStore(null);
  const { tocNarratives, setTocNarratives } = useNarrativesContext();
  const projectName =
    (projectNameData && JSON.parse(projectNameData).project_name) ||
    localStorage.getItem("projectName");

  const [initialInput, setInitialInput] = useState(null);
  const [isLoading, setIsLoading] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isValid, setIsValid] = useState(true);
  const [resultsData, setResultsData] = useState("");
  const [selectedFiles, setSelectedFiles] = useState(null);
  const [base64Images, setBase64Images] = useState<string[]>([]);
  const [isAnswer, setIsAnswer] = useState(false);
  const [sectionOutputPresent, setSectionOutputPresent] = useState(false);

  const results = isSubmitting
    ? ""
    : tocNarratives.find((el) => el.section === sections[+step - 1])?.res;

  const email = localStorage.getItem("email");
  const isFormPage = type === "form";

  const handleSubmit = useCallback(
    async (saveOnly = false) => {
      if (!isValid) return;
      if (isFormPage) {
        setResultsData("");

        const values = form.getFieldsValue();

        if (saveOnly === true) {
          setIsSaving(true);
        } else {
          setIsSubmitting(true);
        }

        const data: DataType = {
          section: sections[+step - 1],
          input: values?.q,
          project_name: projectName,
          email: email,
          model: getSelectedModelType ?? "Titan",
        };

        if (saveOnly === true) {
          data.save_only = true;
        }

        if (base64Images) {
          data.input = {
            text: values?.q,
            images: base64Images,
          };
        }

        console.log("payload body ------------->", data);

        const socket = io(baseSocketUrl, {
          transports: ["websocket", "polling"],
        });

        socketRef.current = socket;
        socketRef?.current?.emit("theoryOfChange", data);

        let timeoutId;
        if (saveOnly === true) {
          timeoutId = setTimeout(() => {
            socket.disconnect();
            setIsSaving(false);
            notification.error("An error occurred: Save request timed out.");
          }, 10000); // 10 seconds timeout
        }

        socketRef?.current?.on("answer", (answer) => {
          const isStopGeneration = localStorage.getItem("isStopGeneration");

          if (
            saveOnly === true &&
            answer === "User input is saved successfully"
          ) {
            clearTimeout(timeoutId); // Cancel timeout if response received
            socket.disconnect();
            setIsSaving(false);
            localStorage.removeItem("isStopGeneration");
            notification.success("Project saved successfully!");
            return nav(constRoute?.existingProject);
          }

          if (!isSubmitting && answer) {
            setIsSubmitting(true);
          }

          if (answer) {
            setIsAnswer(true);
          }

          if (isStopGeneration || answer === "#####") {
            socket.disconnect();
            setIsSubmitting(false);
            setIsAnswer(false);
            localStorage.removeItem("isStopGeneration");
            return;
          }
          form.setFieldValue("q", (form?.getFieldValue("q") ?? "") + answer);
          localStorage.setItem(
            "totalResultsAdvisor" + projectName,
            JSON.stringify({
              ...results,
              [`result${step}`]: (form?.getFieldValue("q") ?? "") + answer,
            }),
          );
        });
        socket.on("disconnect", () => {
          clearTimeout(timeoutId); // Prevent timeout from triggering after disconnect
          localStorage.removeItem("isStopGeneration");
          setIsSubmitting(false);
          setIsAnswer(false);
        });

        if (saveOnly !== true) {
          nav(constRoute?.theoryOfChangeProject + "/results/" + step);
        }
      } else {
        if (saveOnly === true) {
          setIsSaving(true);
          await new Promise((resolve) => setTimeout(resolve, 3000));
          setIsSaving(false);
          notification.success("Project saved successfully!");
          return nav(constRoute?.existingProject);
        }

        if (formQuestions.length < +step + 1) {
          nav(constRoute?.gcfCongratulation + "/theory-of-change");
          return;
        }
        setIsSubmitting(false);
        setIsAnswer(false);
        nav(constRoute?.theoryOfChangeProject + "/form/" + (+step + 1));
      }
    },
    [type, step, getSelectedModelType, base64Images],
  );

  const handleGoBack = () => {
    socketRef?.current?.disconnect();
    setIsSubmitting(false);
    setIsAnswer(false);
    // if (isSubmitting) return;
    if (+step === 1 && type == "results") {
      nav(constRoute?.theoryOfChangeProject + "/form/1");

      return;
    }

    if (+step < 2) {
      nav(constRoute.projectName + "/theory-of-change");
      return;
    }

    if (type === "results") {
      nav(constRoute?.theoryOfChangeProject + "/form/" + +step);
    } else {
      nav(constRoute?.theoryOfChangeProject + "/results/" + (+step - 1));
    }
  };

  const handleSaveQuit = () => {
    handleSubmit(true);
  };

  const handleViewReport = () => {
    nav(constRoute?.theoryOfChangeProject + "/results/" + +step);
  };

  const getProjectData = async () => {
    const email = localStorage.getItem("email");
    const data = {
      section: sections[+step - 1],
      project_name: projectName,
      functionality: "theory of change",
      email,
    };
    setIsLoading(true);
    setSectionOutputPresent(false);
    setSelectedFiles(null);

    localStorage.setItem("data-for-tokens", JSON.stringify(data));
    await getSingleProjectData(data)
      .then((state) => {
        setInitialInput(state?.questions);
        if (isFormPage) {
          form.setFieldValue("q", state?.questions.input);

          // TODO: a bug will bring you here
          if (state?.questions?.images) {
            setBase64Images(state?.questions?.images);
          }
        } else {
          if (!isSubmitting) {
            form.setFieldValue(`q`, state?.generated_narratives?.find(Boolean));
          }
        }
        setTocNarratives((prev) =>
          prev.map((el) => {
            if (el.section === sections[+step - 1]) {
              const obj = {
                ...el,
                res:
                  state?.generated_narratives?.map((el) => el).join("") || "",
              };

              if (obj.res !== "") {
                setSectionOutputPresent(true);
              }

              return obj;
            } else {
              return el;
            }
          }),
        );
      })
      .finally(() => setIsLoading(false));
    if (+step < 1 || formQuestions.length + 1 <= +step) {
      nav(constRoute?.gcfCongratulation + "/theory-of-change");
    }
  };

  const handleStopGenerate = () => {
    if (socketRef) {
      socketRef.current.disconnect();
    }
    setIsSubmitting(false);
    setIsAnswer(false);
  };

  useEffect(() => {
    localStorage.removeItem("isStopGeneration");
    getProjectData();

    // return () => {
    //   localStorage.removeItem("isStopGeneration");
    // };
  }, [step, type]);

  useEffect(() => {
    form.resetFields();
  }, [type === "results"]);

  useEffect(() => {
    socketRef?.current?.disconnect();
    form.resetFields();
    setIsSubmitting(false);
    setIsAnswer(false);
    setResultsData(
      tocNarratives.find((el) => el.section === sections[+step - 1])?.res || "",
    );
  }, [step]);

  useEffect(() => {
    return () => {
      socketRef?.current?.disconnect();
    };
  }, []);

  return (
    <div style={{ padding: "30px 52px 122px" }}>
      <TheoryOfChangeForm
        form={form}
        value={results}
        isSubmitting={isSubmitting}
        initialState={initialInput}
        handleSubmit={handleSubmit}
        isFormPage={isFormPage}
        Text={
          isFormPage
            ? formQuestions[+step - 1]?.q
            : formQuestions[+step - 1]?.result || results[`result${step}`] || ""
        }
        placeholder={formQuestions[+step - 1]?.placeholder}
        maxLength={formQuestions[+step - 1]?.limit}
        disabled={type === "results"}
        isItalicFontStyle
        section={sections[+step - 1]}
        setIsValid={setIsValid}
        isValid={isValid}
        handleStopGenerate={handleStopGenerate}
        handleViewReport={handleViewReport}
        sectionOutputPresent={sectionOutputPresent}
        isLoading={isLoading}
        isAnswer={isAnswer}
        selectedFiles={selectedFiles}
        setSelectedFiles={setSelectedFiles}
        base64Images={base64Images}
        setBase64Images={setBase64Images}
        allowImgUpload={false} // Changed from +step === 1 to false to hide upload button
      />
      <CommonFooterButton
        customStyle={{ left: 0 }}
        isLoadingSubmit={false}
        handleSubmit={handleSubmit}
        handlegoback={handleGoBack}
        isSubmitting={isSubmitting}
        isSaving={isSaving}
        handleSaveAndQuit={handleSaveQuit}
        isSubmit={type === "results"}
        // handleQuickNext={constRoute?.contextAndBaselineResults}
        form={form}
        setIsSubmitting={setIsSubmitting}
        disableSubmit={!isValid || isLoading}
      />
    </div>
  );
};

export default memo(observer(TheoryOfChangePage));
